
import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { adminService } from '../../services/admin';
import { APPLICATION_TYPES, APPLICATION_TYPE_LABELS, APPLICATION_FIELDS } from '../../services/applications';
import { useNavigate } from 'react-router-dom';

const buildConfigurationSchema = (type) => {
  const fields = APPLICATION_FIELDS[type] || [];
  const shape = {};
  fields.forEach((field) => {
    let validator = Yup.string();
    if (field.type === 'url') {
      validator = validator.url('Enter a valid URL');
    }
    if (field.required) {
      validator = validator.required(`${field.label} is required`);
    }
    shape[field.name] = validator;
  });
  return Yup.object().shape(shape);
};

const CreateApplication = ({ application, tenantId, isEditMode = false, onSuccess }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState(true);
  const [selectedFields, setSelectedFields] = useState([]);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      tenantId: tenantId || application?.tenantId || '',
      name: application?.name || '',
      type: application?.type || '',
      configuration: application?.configuration || {},
      credentials: application?.credentials || {},
    },
    validationSchema: Yup.lazy((values) =>
      Yup.object({
        tenantId: Yup.string().required('Tenant is required'),
        name: Yup.string()
          .required('Application name is required')
          .min(2, 'Application name must be at least 2 characters'),
        type: Yup.string()
          .required('Application type is required')
          .oneOf(Object.values(APPLICATION_TYPES)),
        configuration: buildConfigurationSchema(values.type),
        credentials: Yup.object(),
      })
    ),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError('');
        setSuccess('');
        
        if (isEditMode && application) {
          await adminService.updateApplication(application.appId, values);
          setSuccess('Application updated successfully!');
        } else {
          await adminService.createApplication(values.tenantId, values);
          setSuccess('Application created successfully!');
        }
        
        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          }
         navigate('/admin/businesses');
        }, 1500);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 
          `Failed to ${isEditMode ? 'update' : 'create'} application`);
      } finally {
        setLoading(false);
      }
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    fetchBusinesses();
    
    if (isEditMode && application && application.type) {
      const fields = APPLICATION_FIELDS[application.type] || [];
      setSelectedFields(fields);
      
      const config = { ...application.configuration };
      const credentials = { ...application.credentials };
      
      fields.forEach(field => {
        if (config[field.name] === undefined) {
          config[field.name] = '';
        }
        if (credentials[field.name] === undefined) {
          credentials[field.name] = '';
        }
      });
      
      formik.setValues({
        tenantId: application.tenantId || '',
        name: application.name || '',
        type: application.type || '',
        configuration: config,
        credentials: credentials,
      });
    }
  }, [application, isEditMode]);

  useEffect(() => {
    if (formik.values.type && !isEditMode) {
      const fields = APPLICATION_FIELDS[formik.values.type] || [];
      setSelectedFields(fields);
      
      const config = {};
      const credentials = {};
      fields.forEach(field => {
        config[field.name] = formik.values.configuration?.[field.name] || '';
        credentials[field.name] = formik.values.credentials?.[field.name] || '';
      });
      
      if (JSON.stringify(config) !== JSON.stringify(formik.values.configuration)) {
        formik.setFieldValue('configuration', config);
      }
      if (JSON.stringify(credentials) !== JSON.stringify(formik.values.credentials)) {
        formik.setFieldValue('credentials', credentials);
      }
    }
  }, [formik.values.type, isEditMode]);

  const fetchBusinesses = async () => {
    try {
      setLoadingBusinesses(true);
      const data = await adminService.getAllBusinesses();
      setBusinesses(data || []);
    } catch (err) {
      console.error('Error fetching businesses:', err);
      setError('Failed to load businesses');
    } finally {
      setLoadingBusinesses(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 3, md: 5 },
      }}
    >
      <Box sx={{ maxWidth: 1150, mx: 'auto' }}>
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.8, textTransform: 'uppercase', fontWeight: 700 }}>
            Applications / Create Application
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.04em' }}>
            Create Application
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and configure a new application for your business.
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1.5fr 1fr' },
            gap: 3,
          }}
        >
          <Box>
            <Card
              sx={{
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.85),
                boxShadow: theme.palette.mode === 'dark' ? '0 8px 18px rgba(2,6,23,0.35)' : '0 10px 25px rgba(15,23,42,0.05)',
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mb: 3 }}>
                  {[1, 2, 3, 4].map((step) => {
                    const isActive = step === 1;
                    const isPassed = step < 2;
                    return (
                      <Box
                        key={step}
                        sx={{
                          flex: '1 1 160px',
                          minWidth: 140,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 1.25,
                          py: 1,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          backgroundColor: isActive ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.08) : 'transparent',
                        }}
                      >
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 13,
                            fontWeight: 700,
                            backgroundColor: isPassed ? 'primary.main' : theme.palette.mode === 'dark' ? '#1e293b' : '#e5e7eb',
                            color: isPassed ? 'primary.contrastText' : theme.palette.text.secondary,
                          }}
                        >
                          {step}
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: isActive ? 'text.primary' : 'text.secondary' }}>
                          {step === 1 && 'Application Details'}
                          {step === 2 && 'Domains & URLs'}
                          {step === 3 && 'Security & Settings'}
                          {step === 4 && 'Review & Create'}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>

                {error && (
                  <Box sx={{ px: 2, py: 1.5, mb: 3, borderRadius: 2, bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main', border: `1px solid ${alpha(theme.palette.error.main, 0.25)}` }}>
                    {error}
                  </Box>
                )}

                {success && (
                  <Box sx={{ px: 2, py: 1.5, mb: 3, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main', border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                    {success}
                  </Box>
                )}

                <Box component="form" onSubmit={(e) => { e.preventDefault(); formik.handleSubmit(); }}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth error={Boolean(formik.touched.tenantId && formik.errors.tenantId)}>
                        <InputLabel shrink>Business Tenant</InputLabel>
                        <Select
                          name="tenantId"
                          value={formik.values.tenantId}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={loading || loadingBusinesses || isEditMode}
                          displayEmpty
                          sx={{ borderRadius: 2, backgroundColor: 'background.paper' }}
                        >
                          <MenuItem value=""><em>Select a business</em></MenuItem>
                          {loadingBusinesses ? (
                            <MenuItem value="" disabled>Loading businesses...</MenuItem>
                          ) : businesses.length === 0 ? (
                            <MenuItem value="" disabled>No businesses available</MenuItem>
                          ) : (
                            businesses.map((business) => (
                              <MenuItem key={business.tenantId} value={business.tenantId}>
                                {business.businessName} ({business.industry})
                              </MenuItem>
                            ))
                          )}
                        </Select>
                        {formik.touched.tenantId && formik.errors.tenantId && (
                          <FormHelperText>{formik.errors.tenantId}</FormHelperText>
                        )}
                        {isEditMode && <FormHelperText>Tenant cannot be changed in edit mode</FormHelperText>}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Application Name"
                        name="name"
                        placeholder="Enter a descriptive name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={loading || !formik.values.tenantId}
                        error={Boolean(formik.touched.name && formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name ? formik.errors.name : ''}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth error={Boolean(formik.touched.type && formik.errors.type)}>
                        <InputLabel shrink>Application Type</InputLabel>
                        <Select
                          name="type"
                          value={formik.values.type}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={loading || !formik.values.tenantId || isEditMode}
                          displayEmpty
                          sx={{ borderRadius: 2, backgroundColor: 'background.paper' }}
                        >
                          <MenuItem value=""><em>Select type</em></MenuItem>
                          {Object.entries(APPLICATION_TYPE_LABELS).map(([value, label]) => (
                            <MenuItem key={value} value={value}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.type && formik.errors.type && (
                          <FormHelperText>{formik.errors.type}</FormHelperText>
                        )}
                        {isEditMode && <FormHelperText>Application type cannot be changed in edit mode</FormHelperText>}
                      </FormControl>
                    </Grid>
                  </Grid>

                  {selectedFields.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                      <Divider sx={{ mb: 3 }} />
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Configuration Settings
                        </Typography>
                        <Chip label={`${selectedFields.length} fields`} color="primary" variant="outlined" />
                      </Stack>

                      <Grid container spacing={2.5}>
                        {selectedFields.map((field) => {
                          const fieldTouched = formik.touched.configuration?.[field.name];
                          const fieldError = formik.errors.configuration?.[field.name];
                          const showError = Boolean(fieldTouched && fieldError);

                          return (
                            <Grid item xs={12} md={field.type === 'textarea' ? 12 : 6} key={field.name}>
                              {field.type === 'select' ? (
                                <FormControl fullWidth error={showError}>
                                  <InputLabel shrink>{field.label}{field.required ? ' *' : ''}</InputLabel>
                                  <Select
                                    name={`configuration.${field.name}`}
                                    value={formik.values.configuration?.[field.name] || ''}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    disabled={loading || (isEditMode && field.readOnly)}
                                    displayEmpty
                                    sx={{ borderRadius: 2, backgroundColor: 'background.paper' }}
                                  >
                                    <MenuItem value=""><em>Select {field.label}</em></MenuItem>
                                    {field.options?.map((option) => (
                                      <MenuItem key={option} value={option}>
                                        {option}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {showError && <FormHelperText>{fieldError}</FormHelperText>}
                                </FormControl>
                              ) : (
                                <TextField
                                  fullWidth
                                  label={`${field.label}${field.required ? ' *' : ''}`}
                                  name={`configuration.${field.name}`}
                                  type={field.type === 'password' ? 'password' : 'text'}
                                  value={formik.values.configuration?.[field.name] || ''}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  disabled={loading || (isEditMode && field.readOnly)}
                                  error={showError}
                                  helperText={showError ? fieldError : ''}
                                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                              )}
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  )}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end" sx={{ mt: 4 }}>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={() => (onSuccess ? onSuccess() : null)}
                      disabled={loading}
                      sx={{ minWidth: 140 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => formik.handleSubmit()}
                      disabled={loading || !formik.isValid || !formik.values.tenantId}
                      sx={{ minWidth: 180 }}
                    >
                      {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Application' : 'Create Application')}
                    </Button>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box>
            <Card
              sx={{
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
                boxShadow: theme.palette.mode === 'dark' ? '0 8px 18px rgba(2,6,23,0.35)' : '0 10px 25px rgba(15,23,42,0.05)',
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Application Preview
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Review the current configuration before saving.
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <Box
                    sx={{
                      width: 150,
                      height: 150,
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '3.2rem',
                      fontWeight: 800,
                      border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    }}
                  >
                    {formik.values.name ? formik.values.name.slice(0, 2).toUpperCase() : 'AP'}
                  </Box>
                </Box>

                <Typography variant="h5" align="center" sx={{ fontWeight: 700, mb: 1 }}>
                  {formik.values.name || 'Application Name'}
                </Typography>

                <Stack spacing={1.5} sx={{ mt: 3 }}>
                  {[
                    { label: 'Tenant', value: businesses.find((b) => b.tenantId === formik.values.tenantId)?.businessName || 'Not selected' },
                    { label: 'Type', value: APPLICATION_TYPE_LABELS[formik.values.type] || 'Not selected' },
                    { label: 'Status', value: 'Ready to create' },
                  ].map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.06 : 0.03),
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.value}</Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateApplication;